import { StrictMode } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { BrowserRouter } from 'react-router';

import { resolveLanguage } from '../i18n';
import { createApiClient } from '../lib/api/api-client';
import { createSession } from '../lib/api/session';
import { SessionProvider } from '../lib/api/session-context';
import type { Features } from '../lib/config/features-context';
import type { TelegramPort } from '../lib/telegram/telegram-port';

import { AppEntry } from './AppEntry';
import { AppProviders } from './AppProviders';
import { SessionGate } from './SessionGate';
import { createI18n } from './i18n';

/** What {@link bootstrap} needs besides Telegram. */
export interface BootstrapOptions {
  /** API origin from the runtime config (`/config.js`). */
  readonly apiOrigin: string;
  /** Product switches from the runtime config (P8 R4); every switch off when omitted. */
  readonly features?: Features | undefined;
}

/**
 * Starts the Mini App: picks the language from Telegram (FXM-01 §7), renders the
 * shell behind the session gate, then tells Telegram the app is ready.
 *
 * The session (S2) exchanges Telegram's `initData` for an access token held in memory only, and
 * renews it. Nothing renders but the opening states until the trader is recognized.
 *
 * Kept apart from `main.tsx` so tests can boot the real app with a fake Telegram port.
 */
export function bootstrap(
  container: HTMLElement,
  telegram: TelegramPort,
  options: BootstrapOptions,
): Root {
  const language = resolveLanguage(telegram.languageCode());
  document.documentElement.lang = language;
  // Two clients on purpose (P10): the exchange client has no token and no renewal, so the
  // exchange can never trigger a renewal of itself (S2 R14, R18).
  const session = createSession({
    telegram,
    exchangeApi: createApiClient({ baseUrl: options.apiOrigin }),
  });
  const api = createApiClient({
    baseUrl: options.apiOrigin,
    accessToken: session.accessToken,
    renewToken: session.renew,
    onSessionEnded: session.end,
  });
  const root = createRoot(container);
  root.render(
    <StrictMode>
      <AppProviders
        telegram={telegram}
        i18n={createI18n(language)}
        api={api}
        features={options.features}
      >
        <SessionProvider session={session}>
          <SessionGate>
            <BrowserRouter>
              <AppEntry />
            </BrowserRouter>
          </SessionGate>
        </SessionProvider>
      </AppProviders>
    </StrictMode>,
  );
  telegram.ready();
  return root;
}
